import { z } from 'zod';
import { CheckCircle2, Inbox, ListChecks, Mail, Users } from 'lucide-react';
import { StatTile } from '../stat-tile';
import { drilldowns } from '../../drilldowns';
import { useDashboardMetric } from '@/hooks/use-dashboard-metric';
import type { TileDefinition } from './types';
import { pct, priorPeriodLabel } from './helpers';

/**
 * The five stat-style tiles share a render shape — a big number + a
 * caption + an optional ▲/▼ delta. They live together in one module
 * because the structural overhead (TileDefinition wiring, useMetric
 * call, drilldown link) is identical; the only per-tile choices are
 * the metric key, polarity, and tone.
 *
 * Each tile is a separate exported `TileDefinition` so the aggregator
 * can import them selectively and a future "stat tile" addition
 * doesn't require touching another module.
 */

const emptyConfig = z.object({}).strict();

export const openTasksTile: TileDefinition = {
  type: 'open_tasks',
  title: 'Open tasks',
  description: 'All tasks currently open, pending, or in progress.',
  icon: Inbox,
  metrics: ['open_tasks'],
  defaultSize: { w: 3, h: 2 },
  minSize: { w: 2, h: 2 },
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'open_tasks',
      from: period.from,
      to: period.to,
      enabled,
    });
    const diff = metric && metric.priorCount != null ? metric.count - metric.priorCount : undefined;
    return (
      <StatTile
        value={metric?.count.toLocaleString()}
        caption="across the organization"
        isLoading={isLoading}
        isError={isError}
        drilldown={drilldowns.openTasks(dashboardCtx)}
        delta={
          diff === undefined
            ? undefined
            : {
                diff,
                // Open count: shrinking is good (queue clearing),
                // growing is bad (backlog rising).
                polarity: 'down-is-good',
                periodLabel: priorPeriodLabel(dashboardCtx.dashPreset),
              }
        }
      />
    );
  },
};

export const completedTasksTile: TileDefinition = {
  type: 'completed_tasks',
  title: 'Completed',
  description: 'Tasks marked completed within the selected date range.',
  icon: CheckCircle2,
  metrics: ['completed_tasks'],
  defaultSize: { w: 3, h: 2 },
  minSize: { w: 2, h: 2 },
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'completed_tasks',
      from: period.from,
      to: period.to,
      enabled,
    });
    const diff = metric && metric.priorCount != null ? metric.count - metric.priorCount : undefined;
    return (
      <StatTile
        value={metric?.count.toLocaleString()}
        isLoading={isLoading}
        isError={isError}
        tone="positive"
        drilldown={drilldowns.completedTasks(dashboardCtx)}
        delta={
          diff === undefined
            ? undefined
            : {
                diff,
                polarity: 'up-is-good',
                periodLabel: priorPeriodLabel(dashboardCtx.dashPreset),
              }
        }
      />
    );
  },
};

export const completionRateTile: TileDefinition = {
  type: 'completion_rate',
  title: 'Completion rate',
  description: 'Completed tasks as a share of completed + open.',
  icon: ListChecks,
  metrics: ['completion_rate'],
  defaultSize: { w: 3, h: 2 },
  minSize: { w: 2, h: 2 },
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'completion_rate',
      from: period.from,
      to: period.to,
      enabled,
    });
    // For completion rate, the "diff" is in percentage points (current%
    // − prior%). pp is the unit operators expect for ratios; we don't
    // divide one rate by another (would over-state small absolute
    // changes near 100%). The current vs prior denominators are
    // asymmetric (see CompletionRateFetcher) — the delta is a directional
    // indicator, not a rigorous comparison.
    const diff =
      metric && metric.priorRate != null ? (metric.rate - metric.priorRate) * 100 : undefined;
    return (
      <StatTile
        value={metric ? pct(metric.rate) : undefined}
        caption={
          metric
            ? `${metric.completed.toLocaleString()} of ${metric.total.toLocaleString()} tasks`
            : undefined
        }
        isLoading={isLoading}
        isError={isError}
        tone="positive"
        drilldown={drilldowns.completionRate(dashboardCtx)}
        delta={
          diff === undefined
            ? undefined
            : {
                diff,
                polarity: 'up-is-good',
                format: (d) => `${Math.abs(d).toFixed(1)}pp`,
                periodLabel: priorPeriodLabel(dashboardCtx.dashPreset),
              }
        }
      />
    );
  },
};

export const referralsCountTile: TileDefinition = {
  type: 'referrals_count',
  title: 'Referrals',
  description: 'Referrals created within the selected date range.',
  icon: Mail,
  metrics: ['referrals_count'],
  defaultSize: { w: 3, h: 2 },
  minSize: { w: 2, h: 2 },
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'referrals_count',
      from: period.from,
      to: period.to,
      enabled,
    });
    const diff = metric && metric.priorCount != null ? metric.count - metric.priorCount : undefined;
    return (
      <StatTile
        value={metric?.count.toLocaleString()}
        isLoading={isLoading}
        isError={isError}
        drilldown={drilldowns.referralsCount(dashboardCtx)}
        delta={
          diff === undefined
            ? undefined
            : {
                diff,
                // Referrals are inbound — neither direction is "good"
                // in isolation. Display the delta in muted text so
                // the user sees the trend, not a value judgment.
                polarity: 'neutral',
                periodLabel: priorPeriodLabel(dashboardCtx.dashPreset),
              }
        }
      />
    );
  },
};

export const activeUsersTile: TileDefinition = {
  type: 'active_users_count',
  title: 'Active users',
  description: 'Active users in your organization.',
  icon: Users,
  metrics: ['active_users_count'],
  defaultSize: { w: 3, h: 2 },
  minSize: { w: 2, h: 2 },
  configSchema: emptyConfig,
  defaultConfig: {},
  render: ({ period, dashboardCtx, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'active_users_count',
      from: period.from,
      to: period.to,
      enabled,
    });
    return (
      <StatTile
        value={metric?.count.toLocaleString()}
        caption="users with access"
        isLoading={isLoading}
        isError={isError}
        drilldown={drilldowns.activeUsers(dashboardCtx)}
      />
    );
  },
};
